<div class="mx-auto max-w-(--breakpoint-2xl) px-3">
  @if (!embeddedMode) {
    <div class="mb-4  rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
      <div class="my-10 text-center">
        <h2 class="text-2xl font-medium text-gray-800 dark:text-white/90">
          @if (event.id) {
            <p>{{ event.name }}</p>
            <p class="text-gray-500 dark:text-white/70">{{ event.start_date | date: 'EEEE, dd/MM/yyyy' }}</p>
          } @else {
            Add Event
          }
        </h2>
      </div>
      <div class="mt-8 flex flex-col gap-4">
        @if (event.id) {
          <div class="pl-6 flex flex-col sm:flex-row justify-end gap-3">
            <app-button
              [label]="applyingTemplate ? 'Applying...' : 'Apply Template'"
              [icon]="CheckCircleIcon"
              [style]="'secondary'"
              [disabled]="!canApplyTemplates || applyingTemplate || isRightSidebarOpen"
              [title]="!canApplyTemplates ? 'Only admins or managers can apply templates' : ''"
              (click)="openTemplateSelector()">
            </app-button>
            <app-button [routerLink]="['/events', event.id]" [label]="'View Event'" [style]="'primary'" [icon]="Eye"></app-button>
          </div>
        }
      </div>
    </div>
  }

  <form [formGroup]="form">
    @if (embeddedMode && event.id) {
      <div class="mb-3 flex flex-col items-end gap-3 sm:flex-row sm:justify-end">
        <app-button
          [label]="applyingTemplate ? 'Applying...' : 'Apply Template'"
          [icon]="CheckCircleIcon"
          [style]="'secondary'"
          [disabled]="!canApplyTemplates || applyingTemplate || isRightSidebarOpen"
          [title]="!canApplyTemplates ? 'Only admins or managers can apply templates' : ''"
          (click)="openTemplateSelector()">
        </app-button>
      </div>
    }

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
      <div class="flex flex-col gap-3 items-start min-w-0">

        <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Event Information
            </h3>
          </div>
          <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
            <!-- Template Selection Section (moved to top) -->
            @if (!event.id) {
              <div class="space-y-5 mb-6">
                <div class="sm:col-span-2">
                  <app-select
                    [control]="templateControl"
                    [choices]="templateChoices$"
                    label="Event Template (Optional)"
                    placeholder="Select a template to pre-populate timeline items"></app-select>

                  @if (!canApplyTemplates) {
                    <p class="mt-2 text-xs text-gray-500">
                      Only admins or managers can apply templates to new events.
                    </p>
                  }

                  <!-- Template Preview Card -->
                  @if (selectedTemplate) {
                    <div class="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
                      <div class="flex items-start justify-between">
                        <div class="flex-1">
                          <div class="flex items-center gap-2 mb-2">
                            <lucide-icon [name]="CheckCircleIcon" [size]="16" class="text-blue-600 dark:text-blue-400"></lucide-icon>
                            <h4 class="text-sm font-medium text-blue-900 dark:text-blue-100">Template Selected</h4>
                          </div>

                          <div class="space-y-2">
                            <div class="flex items-center gap-2">
                              <span class="text-sm font-medium text-blue-800 dark:text-blue-200">{{ selectedTemplate.name }}</span>
                              <span class="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded dark:bg-blue-800 dark:text-blue-200">
                                {{ selectedTemplate.event_type }}
                              </span>
                            </div>

                            @if (selectedTemplate.description) {
                              <p class="text-xs text-blue-700 dark:text-blue-300">{{ selectedTemplate.description }}</p>
                            }

                            @if (selectedTemplate.items && selectedTemplate.items.length > 0) {
                              <div class="mt-3 space-y-2">
                                <div class="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
                                  <lucide-icon [name]="ClockIcon" [size]="12" class="inline mr-1"></lucide-icon>
                                  {{ selectedTemplate.items.length }} timeline item{{ selectedTemplate.items.length !== 1 ? 's' : '' }} will be created:
                                </div>
                                <div class="space-y-1 max-h-40 overflow-y-auto">
                                  @for (item of selectedTemplate.items; track item.id) {
                                    <div class="flex items-start gap-2 text-xs p-2 bg-white dark:bg-gray-800 rounded border border-blue-100 dark:border-blue-800">
                                      <div class="flex-1">
                                        <div class="font-medium text-gray-900 dark:text-gray-100">{{ item.name }}</div>
                                        <div class="text-gray-600 dark:text-gray-400 mt-0.5">
                                          @if (item.offset_amount === 0) {
                                            <span>Event day</span>
                                          } @else {
                                            <span>{{ item.offset_amount }} {{ item.offset_amount === 1 ? (item.offset_unit | slice:0:-1) : item.offset_unit }} {{ item.offset_direction }}</span>
                                          }
                                          <span class="mx-1">•</span>
                                          <span>{{ formatTime(item.time_of_day) }}</span>
                                        </div>
                                      </div>
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        </div>

                        <button
                          type="button"
                          (click)="clearTemplate()"
                          class="ml-3 text-blue-400 hover:text-blue-600 dark:text-blue-300 dark:hover:text-blue-100"
                          title="Clear template selection">
                          <lucide-icon [name]="XIcon" [size]="16"></lucide-icon>
                        </button>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Event Information Fields -->
            <!-- VG-380: Minimum spend / hire override fields require admin-only access -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div class="sm:col-span-2">
                <app-text-input [control]="form.controls.name" [name]="'name'" [placeholder]="'Event Name'" [label]="'Event Name'"></app-text-input>
              </div>
              <div>
                <label class="invisible block mb-2 text-sm font-bold text-gray-700" aria-hidden="true">
                  Venue
                </label>
                <app-select [control]="form.controls.venue_id" [choices]="venueChoices$"></app-select>
              </div>
              <div>
                <app-select [control]="form.controls.category" [choices]="categoryChoices" label="Category"></app-select>
              </div>
              <div>
                <app-select [control]="form.controls.status" [choices]="selectableStatusChoices" label="Status"></app-select>
                @if (showStatusLockedMessage) {
                  <div class="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 mt-1">
                    <lucide-icon [name]="LockIcon" [size]="12"></lucide-icon>
                    <span>Status locked - confirmed events cannot be changed</span>
                  </div>
                }
                @if (statusLockedToOpportunity) {
                  <div class="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 mt-1">
                    <lucide-icon [name]="LockIcon" [size]="12"></lucide-icon>
                    <span>New events start as Opportunity. Move to Provisional/Confirmed from the event page after creation.</span>
                  </div>
                }
              </div>
              <div>
                @if (showConfirmedDateField) {
                  <app-date-picker [control]="form.controls.confirmed_at" [name]="'confirmed_at'" [label]="'Confirmed Date'"></app-date-picker>
                } @else {
                  <div class="min-h-[3.25rem]" aria-hidden="true"></div>
                }
              </div>
              <div>
                @if (ownershipFieldsEnabled) {
                  @if (salesManagerLocked) {
                    <label class="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                      Sales Manager
                    </label>
                    <div class="flex min-h-11 items-center text-base font-medium text-gray-900 dark:text-white">
                      {{ getSalesManagerNameDisplay() }}
                    </div>
                  } @else {
                    <app-select [control]="form.controls.sales_manager_name_id" [choices]="salesManagerNameChoices$" label="Sales Manager"></app-select>
                  }
                } @else {
                  <app-select [control]="form.controls.sales_manager_id" [choices]="managerChoices$" label="Sales Manager"></app-select>
                }
              </div>
              <div>
                <app-select [control]="form.controls.event_manager_id" [choices]="managerChoices$" label="Event Manager"></app-select>
              </div>
              @if (ownershipFieldsEnabled) {
                <div>
                  @if (form.controls.show_around_name_id.disabled) {
                    <label class="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                      Show around
                    </label>
                    <div class="flex min-h-11 items-center text-base font-medium text-gray-900 dark:text-white">
                      {{ getShowAroundNameDisplay() }}
                    </div>
                  } @else {
                    <app-select [control]="form.controls.show_around_name_id" [choices]="showAroundNameChoices$" label="Show around"></app-select>
                  }
                </div>
              }
              <!-- TVG-379: the latest viewing's outcome. Each viewing's own
                   outcome and person are recorded on the contact's Viewings list. -->
              @if (viewingOutcomeLabel) {
                <div data-testid="event-viewing-outcome">
                  <label class="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400">
                    Viewing outcome
                  </label>
                  <div class="flex min-h-11 items-center text-base font-medium text-gray-900 dark:text-white">
                    {{ viewingOutcomeLabel }}
                  </div>
                </div>
              }
              <!-- TVG-259: The manual "Source Quote ID" input has been removed from the
                   Create Event form. Staff never hand-type quote IDs and a mistyped or
                   duplicate value only surfaced as a validation error at create time.
                   The `venue_quote_id` FormControl is deliberately kept in the component
                   (event-edit.component.ts) and is still auto-populated by the
                   Convert-to-event flow (prefillFromQuote) and the `venue_quote_id`
                   query param (applyCreateEventQueryParams), so EventController::store
                   keeps importing the quote's products on save. See TVG-120 for that flow. -->
              <div class="sm:col-span-2">
                <app-textarea [control]="form.controls.description" [name]="'description'" [placeholder]="'Description'" [label]="'Description'"></app-textarea>
              </div>
              <!--            <div class="w-full px-2.5 xl:w-1/2">-->
              <!--              <app-text-input [control]="form.controls.menu_code_string" [name]="'menu_code_string'" [placeholder]="'Menu Code'" [label]="'Menu Code'"></app-text-input>-->
              <!--            </div>-->
              <!--            <div class="w-full px-2.5 xl:w-1/2">-->
              <!--              <app-text-input [control]="form.controls.special_offers_string" [name]="'special_offers_string'" [placeholder]="'Special Offers'"-->
              <!--                              [label]="'Special Offers'"></app-text-input>-->
              <!--            </div>-->

              <div>
                <app-checkbox [control]="form.controls.show_on_board" [label]="'Show on Deals'"></app-checkbox>
              </div>
            </div>
          </div>
        </div>

        @if (false) {
          <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Participants
              </h3>
            </div>
            <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6 overflow-hidden">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <!--            <div class="w-full px-2.5 xl:w-1/2">-->
                <!--              <app-text-input [control]="form.controls.bride" [name]="'bride'" [placeholder]="'Bride'" [label]="'Bride'"></app-text-input>-->
                <!--            </div>-->
                <!--            <div class="w-full px-2.5 xl:w-1/2">-->
                <!--              <app-text-input [control]="form.controls.groom" [name]="'groom'" [placeholder]="'Groom'" [label]="'Groom'"></app-text-input>-->
                <!--            </div>-->
                <div>
                  <app-text-input [control]="form.controls.brides_parents" [name]="'brides_parents'" [placeholder]="'Bride\'s Parents'" [label]="'Bride\'s Parents'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.grooms_parents" [name]="'grooms_parents'" [placeholder]="'Groom\'s Parents'" [label]="'Groom\'s Parents'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.maid_of_honour" [name]="'maid_of_honour'" [placeholder]="'Maid of Honour'" [label]="'Maid of Honour'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.bridesmaids" [name]="'bridesmaids'" [placeholder]="'Bridesmaids'" [label]="'Bridesmaids'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.flower_girls" [name]="'flower_girls'" [placeholder]="'Flower Girls'" [label]="'Flower Girls'"></app-text-input>
                </div>
                <div>
                  <app-checkbox [control]="form.controls.flower_girls_walking" [label]="'Flower Girls Walking'"></app-checkbox>
                </div>
                <div>
                  <app-text-input [control]="form.controls.paige_boys" [name]="'paige_boys'" [placeholder]="'Paige Boys'" [label]="'Paige Boys'"></app-text-input>
                </div>
                <div>
                  <app-checkbox [control]="form.controls.paige_boys_walking" [label]="'Paige Boys Walking'"></app-checkbox>
                </div>
                <div>
                  <app-text-input [control]="form.controls.best_men" [name]="'best_men'" [placeholder]="'Best Men'" [label]="'Best Men'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.groomsmen" [name]="'groomsmen'" [placeholder]="'Groomsmen'" [label]="'Groomsmen'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.bride_escort" [name]="'bride_escort'" [placeholder]="'Bride Escort'" [label]="'Bride Escort'"></app-text-input>
                </div>
                <div>
                  <app-text-input [control]="form.controls.bridal_party_entrance_order" [name]="'bridal_party_entrance_order'" [placeholder]="'Bridal Party Entrance Order'"
                                  [label]="'Bridal Party Entrance Order'"></app-text-input>
                </div>
              </div>
            </div>
          </div>
        }

      </div>

      <div class="flex flex-col gap-3 items-start min-w-0">

        <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Event Dates & Times
            </h3>
          </div>
          <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <app-date-picker [control]="form.controls.start_date" [name]="'start_date'" [label]="'Event Date'" (dateChanged)="onDateChanged($event)"></app-date-picker>
                @if (showDateLockedMessage) {
                  <div class="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 mt-1">
                    <lucide-icon [name]="LockIcon" [size]="12"></lucide-icon>
                    <span>Event date locked - confirmed events cannot be changed</span>
                  </div>
                }
              </div>
              <div>
                <app-date-picker [control]="form.controls.end_date" [name]="'end_date'" [label]="'End Date'"></app-date-picker>
              </div>
              <div>
                <app-date-picker [control]="form.controls.arrival" [name]="'arrival'" [label]="'Arrival Date'"></app-date-picker>
              </div>
              <div>
                <app-time-picker [control]="form.controls.arrival_time" [name]="'arrival_time'" [label]="'Arrival Time'"></app-time-picker>
              </div>
              <div>
                <app-date-picker [control]="form.controls.departure" [name]="'departure'" [label]="'Departure Date'"></app-date-picker>
              </div>
              <div>
                <app-time-picker [control]="form.controls.departure_time" [name]="'departure_time'" [label]="'Departure Time'"></app-time-picker>
              </div>
              <div>
                <app-date-picker [control]="form.controls.show_round" [name]="'show_round'" [label]="'Show Round Date'"></app-date-picker>
              </div>
              <div>
                <app-time-picker [control]="form.controls.show_round_time" [name]="'show_round_time'" [label]="'Show Round Time'"></app-time-picker>
              </div>
            </div>
          </div>
        </div>

        <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div class="px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Prices
            </h3>
          </div>
          <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
            <!-- Daily Price Display -->
            @if (showDailyPrices) {
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                <div>
                  <label class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                    £ Minimum spend
                  </label>
                  <div class="mt-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
                    £{{ dailyMinimumSpend | number:'1.2-2' }}
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium leading-6 text-gray-900 dark:text-white">
                    £ Hire price
                  </label>
                  <div class="mt-2 text-sm text-gray-700 dark:text-gray-300 font-medium">
                    £{{ dailyHirePrice | number:'1.2-2' }}
                  </div>
                </div>
              </div>
            }

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div data-testid="minimum-spend-override">
                <app-text-input
                  [control]="form.controls.minimum_spend_override"
                  [mask]="'separator.2'"
                  [thousandSeparator]="','"
                  [name]="'price'"
                  [placeholder]="'0.00'"
                  [label]="'&pound; Minimum spend override'"
                  [disabled]="!canEditVenueOverrides"
                  [helpText]="!canEditVenueOverrides ? 'Only admins can override venue pricing.' : ''"
                ></app-text-input>
              </div>
              <div data-testid="hire-price-override">
                <app-text-input
                  [control]="form.controls.hire_price_override"
                  [mask]="'separator.2'"
                  [thousandSeparator]="','"
                  [name]="'price'"
                  [placeholder]="'0.00'"
                  [label]="'&pound; Venue Hire Price Override'"
                  [disabled]="!canEditVenueOverrides"
                  [helpText]="!canEditVenueOverrides ? 'Only admins can override venue pricing.' : ''"
                ></app-text-input>
              </div>
              <div>
                <app-text-input [control]="form.controls.reservation_fee" [mask]="'separator.2'" [thousandSeparator]="','" [name]="'price'" [placeholder]="'0.00'"
                                [label]="'&pound; Reservation Fee'"></app-text-input>
              </div>
              <div>
                <app-text-input [control]="form.controls.deposit" [mask]="'separator.2'" [thousandSeparator]="','" [name]="'price'" [placeholder]="'0.00'"
                                [label]="'&pound; Deposit (Security Retainer)'"></app-text-input>
              </div>
            </div>
          </div>

        </div>


        <div class="rounded-2xl w-full border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]" data-testid="event-contacts-section">
          <div class="px-5 py-4 sm:px-6 sm:py-5">
            <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
              Contacts
            </h3>
          </div>
          <div class="p-5 space-y-6 border-t border-gray-100 dark:border-gray-800 sm:p-6">
            <div class="space-y-3">
              <div class="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div class="relative z-50 flex-1">
                  <app-searchable-select
                    [control]="form.controls.contacts_ids"
                    [multiple]="true"
                    [searchFunction]="contactSearchFunction"
                    [initialOptions]="initialContactChoices"
                    [placeholder]="'Select Contacts'"
                    [searchPlaceholder]="'Search contacts...'"
                    [minSearchLength]="2"
                    [height]="'11'"
                    [enableOptionLinks]="true"
                    [optionLinkGenerator]="contactLinkGenerator"
                    (optionLinkClick)="onContactLinkClick($event)">
                  </app-searchable-select>
                </div>
                <div class="shrink-0">
                  <button
                    type="button"
                    class="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    (click)="openContactModal()">
                    <lucide-icon [name]="PlusIcon" [size]="18"></lucide-icon>
                    Create New Contact
                  </button>
                </div>
              </div>
              @if (selectedContacts.length > 0) {
                <div class="space-y-3 pt-2">
                  @for (contact of selectedContacts; track contact.id) {
                    <div class="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40 sm:flex-row sm:items-start sm:justify-between" data-testid="selected-contact-card">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-semibold text-white">
                        {{ contact.initials }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-semibold text-gray-900 dark:text-white">
                          {{ contact.name }}
                        </p>
                        <div class="mt-2 flex flex-col gap-2 text-sm text-gray-600 dark:text-gray-300">
                          @if (contact.email) {
                            <div class="flex items-center gap-2 min-w-0">
                              <lucide-icon [name]="MailIcon" [size]="16" class="shrink-0 text-gray-400 dark:text-gray-500"></lucide-icon>
                              <a
                                [href]="getContactMailto(contact)"
                                class="min-w-0 truncate text-brand-700 transition-colors hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200">
                                {{ contact.email }}
                              </a>
                            </div>
                          }
                          @if (contact.phone) {
                            <div class="flex items-center gap-2 min-w-0">
                              <lucide-icon [name]="PhoneIcon" [size]="16" class="shrink-0 text-gray-400 dark:text-gray-500"></lucide-icon>
                              <a
                                [href]="getContactTel(contact)"
                                class="text-brand-700 transition-colors hover:text-brand-800 dark:text-brand-300 dark:hover:text-brand-200">
                                {{ contact.phone }}
                              </a>
                            </div>
                          }
                        </div>
                      </div>
                      <div class="flex shrink-0 items-center gap-2 sm:pt-0.5">
                        <button
                          type="button"
                          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                          (click)="editSelectedContact(contact.id)">
                          <lucide-icon [name]="EditIcon" [size]="16"></lucide-icon>
                          Edit
                        </button>
                        <button
                          type="button"
                          class="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/60 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-950/20"
                          (click)="removeSelectedContact(contact.id)">
                          <lucide-icon [name]="TrashIcon" [size]="16"></lucide-icon>
                          Remove
                        </button>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>

      </div>
    </div>

    <!--  @if (validationErrors.length > 0) {-->
    <!--    <div class="mt-4 p-4 border border-red-500 bg-red-100 text-red-700 rounded-lg">-->
    <!--      <h3 class="font-medium">Validation Errors:</h3>-->
    <!--      <ul class="list-disc list-inside">-->
    <!--        @for (error of validationErrors; track error) {-->
    <!--          <li>{{ error }}</li>-->
    <!--        }-->
    <!--      </ul>-->
    <!--    </div>-->
    <!--  }-->

    @if (!embeddedMode) {
      <div class="mt-8 flex flex-row items-center justify-end gap-3">
        <app-button
          [label]="'Cancel'"
          [icon]="XIcon"
          [style]="'secondary'"
          (click)="cancel()"
        ></app-button>

        <app-button
          [label]="'Save & Continue'"
          [icon]="CheckCircleIcon"
          [style]="'secondary'"
          [disabled]="!canSave || processing"
          (click)="saveAndContinue()"
        ></app-button>

        <app-button
          [label]="'Save & View'"
          [icon]="Eye"
          [style]="'secondary'"
          [disabled]="!canSave || processing"
          (click)="saveAndView()"
        ></app-button>

        <app-button
          [label]="'Save'"
          [icon]="CheckCircleIcon"
          [style]="'primary'"
          [disabled]="!canSave || processing"
          (click)="save()"
        ></app-button>
      </div>
    } @else {
      <div class="mt-8 flex flex-row items-center justify-end gap-3">
        <app-button
          [label]="'Save Event Details'"
          [icon]="CheckCircleIcon"
          [style]="'primary'"
          [disabled]="!canSave || processing"
          (click)="save()"
        ></app-button>
      </div>
    }
  </form>
</div>

<app-right-sidebar [isSidebarOpen]="isRightSidebarOpen" [customWidth]="35" (closed)="closeRightSidebar()">
  @if (rightSidebarContent === 'contact-form') {
    <app-contact-form
      (contactCreated)="onContactCreated($event)"
      (cancelled)="closeRightSidebar()">
    </app-contact-form>
  }
  @if (canApplyTemplates && rightSidebarContent === 'template-selection') {
    <app-template-selection-form
      [eventId]="event.id"
      (templateApplied)="onTemplateApplied($event)"
      (cancelled)="closeRightSidebar()">
    </app-template-selection-form>
  }
</app-right-sidebar>

<!-- VG-342: Price Locking Warning Modal - only shown when PRICE_LOCKING flag is true -->
<app-price-locking-warning-modal
  [isOpen]="showPriceLockingModal"
  [showConfirmedDate]="showConfirmedDateInModal"
  [confirmedDateControl]="form.controls.confirmed_at"
  (confirmed)="onPriceLockingConfirmed()"
  (cancelled)="onPriceLockingCancelled()">
</app-price-locking-warning-modal>
<!-- END VG-342 -->
